/**
 * Main App Component
 * Sets up routing and provides context providers
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import EmployeeDashboard from './pages/EmployeeDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import logger from './utils/logger';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { user, loading, hasRole } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-primary-50 to-primary-100">
        <div className="text-center">
          <div className="mb-6 flex justify-center">
            <img 
              src="/Ignite_Logo.png" 
              alt="IDT IGNITE QUARKS" 
              className="h-16 w-auto max-w-[200px]"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    logger.warning('Unauthorized access attempt', { requiredRole });
    return <Navigate to="/login" replace />;
  }
  
  if (requiredRole && !hasRole(requiredRole)) {
    logger.warning('Insufficient permissions', { userId: user.id, requiredRole, userRole: user.role });
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Dashboard Router - redirects based on user role
const DashboardRouter = () => {
  const { user, isAdmin, isManager } = useAuth();
  
  if (isAdmin()) {
    return <Navigate to="/admin" replace />;
  }
  
  if (isManager()) {
    return <Navigate to="/manager" replace />;
  }
  
  return <Navigate to="/employee" replace />;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/employee"
        element={
          <ProtectedRoute>
            <Layout>
              <EmployeeDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/manager"
        element={
          <ProtectedRoute requiredRole="manager">
            <Layout>
              <ManagerDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="admin">
            <Layout>
              <AdminDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const App = () => {
  logger.info('App initialized', { operation: 'App' });
  
  return (
    <Router>
      <AuthProvider>
        <AppProvider>
          <div className="App">
            <AppRoutes />
          </div>
        </AppProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;

